import { Popover } from 'antd';
import { HashLink } from 'react-router-hash-link';
import classNames from 'classnames';
import { useMemo, useState, type FC } from 'react';

import { useMiniNav } from './hooks/use-mini-nav';
import s from './styles.module.scss';

import type { IRpmActivityCountResponse } from 'infrastructure/interfaces/i-rpm-patient-activity';
import type { ModuleType } from 'infrastructure/enums';

interface MiniNavProps {
  module: ModuleType;
  activityCount?: IRpmActivityCountResponse;
}

const smoothScroll = (target: number, duration = 500) => {
  const start = window.scrollY;
  const distance = target - start;
  let startTime: number | null = null; // track the start time of the animation

  const scroll = (currentTime: number) => {
    if (!startTime) startTime = currentTime; // if it's the first frame, set the start time
    const timeElapsed = currentTime - startTime; // time elapsed since the start of the animation
    const progress = Math.min(timeElapsed / duration, 1); // progress of the animation (clamped between 0 and 1)
    const easing = progress * (2 - progress); // smooth deceleration easeOutQuad
    window.scrollTo(0, start + distance * easing); // update the scroll position based on easing

    // continue animation until the duration is reached
    if (timeElapsed < duration) {
      requestAnimationFrame(scroll);
    }
  };

  requestAnimationFrame(scroll); // start the animation
};

const scrollWithOffset = (el: HTMLElement) => {
  const yCoordinate = el.getBoundingClientRect().top + window.scrollY;
  const yOffset = -110; // header + mini banner height
  smoothScroll(yCoordinate + yOffset);
};

const MiniNav: FC<MiniNavProps> = ({ module, activityCount }) => {
  const { linksList, generateLinksWithIndicators } = useMiniNav();
  const [visiblePopover, setVisiblePopover] = useState<string | null>(null);
  const [isReadyToOpen, setIsReadyToOpen] = useState<boolean>(true);

  const links = useMemo(
    () => generateLinksWithIndicators(linksList, module, activityCount),
    [linksList, module, activityCount],
  );

  return (
    <div className={s.wrapper}>
      {links.map((link, idx, arr) => (
        <div key={link.key}>
          <Popover
            placement="right"
            trigger="hover"
            title={link.title}
            arrow={false}
            getPopupContainer={() => document.body}
            open={visiblePopover === link.to && isReadyToOpen}
            onOpenChange={(open) => {
              if (!open) setVisiblePopover(null);
              else if (visiblePopover !== link.to) setVisiblePopover(link.to);
            }}
          >
            <HashLink
              to={link.to}
              scroll={(el: HTMLElement) => {
                scrollWithOffset(el);
              }}
              onClick={() => {
                setIsReadyToOpen(false);
                setVisiblePopover(null);
                setTimeout(() => {
                  setVisiblePopover(null);
                  setIsReadyToOpen(true);
                }, 2500);
              }}
            >
              <div
                className={classNames(
                  s.item,
                  link.iconColor && s[link.iconColor],
                  {
                    [s.marked]: link.marked && link.iconColor,
                  },
                )}
                data-cy={link.dataCy}
              >
                {link.icon}
              </div>
            </HashLink>
          </Popover>
          {idx !== arr.length - 1 && <div className={s.divider} />}
        </div>
      ))}
    </div>
  );
};
export default MiniNav;
